import { useEffect, useMemo, useRef, useState } from "react";
import JicFloatingWidget from "./jicFloatingWidget";
import {
  fetchJicHandle,
  getJicToken,
  getJicUser,
  loginJic,
  onJicChange,
  type JicUser,
} from "../lib/jic";
import {
  describeFace,
  loadFaceRecognitionModels,
  saveLocalFaceRecord,
} from "../lib/faceRecognition";
import NameSigil from "./nameSigil";
import { deleteAvatar, prepareAvatar, uploadAvatar } from "../lib/avatar";
import {
  bindFaceOnServer,
  fetchFaceStatus,
  resolveHandleAddress,
  setFacePublicOnServer,
  type ServerFaceRecord,
} from "../lib/faceRegistry";

type Landmark = { x: number; y: number; z?: number };
type FaceMeshResults = { multiFaceLandmarks?: Landmark[][] };
type FaceMeshInstance = {
  setOptions: (options: Record<string, unknown>) => void;
  onResults: (handler: (results: FaceMeshResults) => void) => void;
  send: (input: { image: HTMLVideoElement }) => Promise<void>;
  close?: () => Promise<void> | void;
};
type FaceMeshConstructor = new (options: {
  locateFile: (file: string) => string;
}) => FaceMeshInstance;

declare global {
  interface Window {
    FaceMesh?: FaceMeshConstructor;
  }
}

type Challenge = "blink" | "left" | "right";
type CheckState = "idle" | "starting" | "active" | "success" | "error";

const CHALLENGE_COPY: Record<Challenge, string> = {
  blink: "自然眨眼一次",
  left: "缓慢向左转头",
  right: "缓慢向右转头",
};

const FACE_MESH_CDN =
  "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/";
const LIVENESS_METHOD = "face-mesh-challenge-v0";

function distance(a: Landmark, b: Landmark) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function eyeRatio(points: Landmark[], top: number, bottom: number, a: number, b: number) {
  return distance(points[top], points[bottom]) / Math.max(distance(points[a], points[b]), 0.001);
}

function shuffledChallenges(): Challenge[] {
  const values: Challenge[] = ["blink", "left", "right"];
  for (let index = values.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [values[index], values[swap]] = [values[swap], values[index]];
  }
  return values;
}

function shortAddress(value: string) {
  return value.length > 14 ? `${value.slice(0, 7)}…${value.slice(-5)}` : value;
}

function FaceSigil({ live = false }: { live?: boolean }) {
  return (
    <svg className={`face3-sigil ${live ? "is-live" : ""}`} viewBox="0 0 320 390" role="img" aria-label="FACE3 面纹">
      <defs>
        <radialGradient id="faceGlow" cx="50%" cy="42%" r="62%">
          <stop offset="0" stopColor="#ff8ec8" stopOpacity=".32" />
          <stop offset=".52" stopColor="#7949ff" stopOpacity=".12" />
          <stop offset="1" stopColor="#071019" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="faceLine" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#58e8ff" />
          <stop offset=".48" stopColor="#ffffff" />
          <stop offset="1" stopColor="#ff66ad" />
        </linearGradient>
        <filter id="lineGlow"><feGaussianBlur stdDeviation="2.2" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <ellipse cx="160" cy="190" rx="150" ry="178" fill="url(#faceGlow)" />
      <path className="sigil-faint" d="M160 30C88 30 50 90 50 174c0 100 47 179 110 190 63-11 110-90 110-190C270 90 232 30 160 30Z" />
      <g fill="none" stroke="url(#faceLine)" strokeLinecap="round" strokeLinejoin="round" filter="url(#lineGlow)">
        <path d="M160 63c-31 7-55 27-70 59 21-14 42-20 64-18" />
        <path d="M160 63c31 7 55 27 70 59-21-14-42-20-64-18" />
        <path d="M73 143c23-6 48-2 70 13-19 14-44 18-67 9" />
        <path d="M247 143c-23-6-48-2-70 13 19 14 44 18 67 9" />
        <path d="M132 117c10 35 7 78-11 116 16 12 29 14 39 4" />
        <path d="M188 117c-10 35-7 78 11 116-16 12-29 14-39 4" />
        <path d="M89 188c7 51 29 96 71 130" />
        <path d="M231 188c-7 51-29 96-71 130" />
        <path d="M116 269c17-5 31-2 44 8 13-10 27-13 44-8-13 24-28 34-44 34s-31-10-44-34Z" />
        <path className="sigil-heart" d="M160 323c-8-12-22-16-31-7-12 13 1 30 31 48 30-18 43-35 31-48-9-9-23-5-31 7Z" />
      </g>
      <g className="sigil-nodes" fill="#fff">
        <circle cx="73" cy="143" r="3" /><circle cx="247" cy="143" r="3" />
        <circle cx="121" cy="233" r="3" /><circle cx="199" cy="233" r="3" />
        <circle cx="160" cy="364" r="3" />
      </g>
    </svg>
  );
}

export default function FaceProfile({ handle }: { handle: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const meshRef = useRef<FaceMeshInstance | null>(null);
  const frameRef = useRef<number | null>(null);
  const runningRef = useRef(false);
  const challengeRef = useRef<Challenge[]>([]);
  const stepRef = useRef(0);
  const stableFramesRef = useRef(0);
  const blinkReadyRef = useRef(false);
  const capturePendingRef = useRef(false);
  const finishingRef = useRef(false);
  const [checkState, setCheckState] = useState<CheckState>("idle");
  const [cameraMessage, setCameraMessage] = useState("镜头只在本机处理，不上传视频");
  const [step, setStep] = useState(0);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [facePresent, setFacePresent] = useState(false);
  const [jicUser, setJicUser] = useState<JicUser | null>(() => getJicUser());
  const [ownerAddress, setOwnerAddress] = useState<string | null>(null);
  const [ownerChecked, setOwnerChecked] = useState(false);
  const [myName, setMyName] = useState<string | null>(null);
  const [descriptor, setDescriptor] = useState<number[] | null>(null);
  const [serverRecord, setServerRecord] = useState<ServerFaceRecord | null>(null);
  const [binding, setBinding] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  const [wallPublic, setWallPublic] = useState(false);
  const [wallBusy, setWallBusy] = useState(false);
  const [wallError, setWallError] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);

  const livenessPassed = checkState === "success";
  const currentChallenge = challenges[step];
  const capturingTemplate = checkState === "active" && step >= challenges.length && challenges.length > 0;
  const bound = Boolean(serverRecord);

  useEffect(() => onJicChange(setJicUser), []);

  // 页面名字 → 链上持有者地址 + 服务端绑定状态
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [address, status] = await Promise.all([
        resolveHandleAddress(handle),
        fetchFaceStatus(handle),
      ]);
      if (cancelled) return;
      setOwnerAddress(address);
      setOwnerChecked(true);
      if (status?.bound && status.record) setServerRecord(status.record);
      setWallPublic(Boolean(status?.public));
      setAvatar(status?.avatar || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  // 登录后查自己的短名字,用于「这不是你的页面」提示
  useEffect(() => {
    let cancelled = false;
    if (!jicUser?.sub || !/^0x[a-fA-F0-9]{40}$/.test(jicUser.sub)) {
      setMyName(null);
      return;
    }
    fetchJicHandle(jicUser.sub).then((name) => {
      if (!cancelled) setMyName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [jicUser?.sub]);

  const isOwner = Boolean(
    jicUser?.sub && ownerAddress && jicUser.sub.toLowerCase() === ownerAddress.toLowerCase()
  );

  const stopCamera = () => {
    runningRef.current = false;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    meshRef.current?.close?.();
    meshRef.current = null;
  };

  useEffect(() => stopCamera, []);

  const finishEnrollment = async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    setCameraMessage(`正在本机生成 @${handle} 的 Face ID 模板…`);
    try {
      const captured = videoRef.current ? await describeFace(videoRef.current) : null;
      if (!captured) throw new Error("没有取得清晰正脸，请重新开始活体检测");
      setDescriptor(captured);
      setCheckState("success");
      setCameraMessage("活体完成 · 登录 JIC 后即可写入 Registry");
    } catch (error) {
      setCheckState("error");
      setCameraMessage(error instanceof Error ? error.message : "Face ID 模板生成失败");
    } finally {
      capturePendingRef.current = false;
      stopCamera();
    }
  };

  const completeChallenge = () => {
    stableFramesRef.current = 0;
    blinkReadyRef.current = false;
    const next = stepRef.current + 1;
    stepRef.current = next;
    setStep(next);
    if (next >= challengeRef.current.length) {
      capturePendingRef.current = true;
      stableFramesRef.current = 0;
      setCameraMessage("动作完成，请回到正面并保持稳定");
    }
  };

  const handleLandmarks = (points: Landmark[]) => {
    if (points.length < 468) return;
    const faceWidth = distance(points[234], points[454]);
    if (faceWidth < 0.18) {
      setCameraMessage("请靠近一点，让脸保持在面纹框内");
      stableFramesRef.current = 0;
      return;
    }

    const leftEye = eyeRatio(points, 159, 145, 33, 133);
    const rightEye = eyeRatio(points, 386, 374, 362, 263);
    const eyes = (leftEye + rightEye) / 2;
    const leftEdge = points[234].x;
    const rightEdge = points[454].x;
    const low = Math.min(leftEdge, rightEdge);
    const width = Math.max(Math.abs(rightEdge - leftEdge), 0.001);
    const yaw = (points[1].x - low) / width;

    if (capturePendingRef.current) {
      const centered = yaw > 0.44 && yaw < 0.56;
      stableFramesRef.current = centered ? stableFramesRef.current + 1 : 0;
      setCameraMessage(centered ? "保持正面，正在生成 Face ID" : "请回到正面看向镜头");
      if (stableFramesRef.current >= 5) void finishEnrollment();
      return;
    }

    const challenge = challengeRef.current[stepRef.current];
    if (!challenge) return;

    let passedFrame = false;
    if (challenge === "blink") {
      if (eyes > 0.2) blinkReadyRef.current = true;
      passedFrame = blinkReadyRef.current && eyes < 0.145;
      setCameraMessage(blinkReadyRef.current ? "请自然眨眼一次" : "睁眼看向镜头");
    } else if (challenge === "left") {
      passedFrame = yaw > 0.59;
      setCameraMessage("缓慢向左转头，保持一下");
    } else {
      passedFrame = yaw < 0.41;
      setCameraMessage("缓慢向右转头，保持一下");
    }

    stableFramesRef.current = passedFrame ? stableFramesRef.current + 1 : 0;
    const needed = challenge === "blink" ? 1 : 4;
    if (stableFramesRef.current >= needed) completeChallenge();
  };

  const startLiveness = async () => {
    setCheckState("starting");
    setCameraMessage("正在连接摄像头…");
    setFacePresent(false);
    setBindError(null);
    const nextChallenges = shuffledChallenges();
    challengeRef.current = nextChallenges;
    stepRef.current = 0;
    stableFramesRef.current = 0;
    blinkReadyRef.current = false;
    capturePendingRef.current = false;
    finishingRef.current = false;
    setChallenges(nextChallenges);
    setStep(0);

    try {
      if (!window.FaceMesh) throw new Error("活体模型尚未加载，请刷新页面后重试");
      void loadFaceRecognitionModels().catch(() => undefined);
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("当前浏览器不支持摄像头，请使用最新版 Safari 或 Chrome");
      }
      const cameraRequest = navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 900 } },
        audio: false,
      });
      const stream = await Promise.race([
        cameraRequest,
        new Promise<never>((_, reject) =>
          window.setTimeout(
            () => reject(new Error("摄像头授权超时，请允许访问后重新开始")),
            20000
          )
        ),
      ]);
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error("摄像头画面初始化失败");
      video.srcObject = stream;
      await video.play();

      const mesh = new window.FaceMesh({ locateFile: (file) => `${FACE_MESH_CDN}${file}` });
      mesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.68,
        minTrackingConfidence: 0.68,
      });
      mesh.onResults((results) => {
        const points = results.multiFaceLandmarks?.[0];
        setFacePresent(Boolean(points));
        if (points) handleLandmarks(points);
        else setCameraMessage("没有检测到脸，请正对镜头");
      });
      meshRef.current = mesh;
      runningRef.current = true;
      setCheckState("active");

      const analyze = async () => {
        if (!runningRef.current || !videoRef.current || !meshRef.current) return;
        if (videoRef.current.readyState >= 2) {
          try {
            await meshRef.current.send({ image: videoRef.current });
          } catch {
            // A dropped inference frame should not end the challenge.
          }
        }
        frameRef.current = requestAnimationFrame(analyze);
      };
      frameRef.current = requestAnimationFrame(analyze);
    } catch (error) {
      stopCamera();
      setCheckState("error");
      setCameraMessage(error instanceof Error ? error.message : "无法启动活体检测");
    }
  };

  const bindIdentity = async () => {
    setBindError(null);
    const token = getJicToken();
    if (!jicUser?.sub || !token) {
      loginJic({ clearLocal: false });
      return;
    }
    if (!isOwner) {
      setBindError(
        myName
          ? `你登录的账户持有 @${myName}，请到 face.jic.io/${myName} 认领自己的脸`
          : "你登录的账户还没有 JIC 短名字，先到 u.jic.io 认领一个短名字"
      );
      return;
    }
    if (!descriptor) {
      setBindError("Face ID 模板缺失，请重新完成活体检测");
      return;
    }
    setBinding(true);
    try {
      const result = await bindFaceOnServer(descriptor, token, LIVENESS_METHOD);
      if (!result.ok || !result.record) {
        throw new Error(
          result.error === "face already bound"
            ? `这张脸已经绑定在 ${result.boundTo || "另一个名字"} 上`
            : result.error || "服务端绑定失败"
        );
      }
      setServerRecord(result.record);
      // 本机也留一份缓存,离线时扫描端可以回退匹配
      saveLocalFaceRecord({
        faceId: `@${result.record.name}`,
        handle: result.record.name,
        descriptor,
        enrolledAt: result.record.enrolledAt,
        storage: "local-device",
        controllerAddress: result.record.address,
        addressVerified: true,
      });
    } catch (error) {
      setBindError(error instanceof Error ? error.message : "服务端绑定失败");
    } finally {
      setBinding(false);
    }
  };

  // 锚点墙开关:只有本人能改;墙上只显示 @名字 + 名字生成的图案
  const toggleWall = async (on: boolean) => {
    setWallError(null);
    const token = getJicToken();
    if (!token) {
      loginJic({ clearLocal: false });
      return;
    }
    setWallBusy(true);
    try {
      const result = await setFacePublicOnServer(on, token);
      if (!result.ok) throw new Error(result.error || "设置失败");
      setWallPublic(Boolean(result.public));
    } catch (error) {
      setWallError(error instanceof Error ? error.message : "设置失败");
    } finally {
      setWallBusy(false);
    }
  };

  // 公开头像:本人上传/更换/移除;上传前在本机裁剪压缩并去掉位置信息
  const withToken = () => {
    const token = getJicToken();
    if (!token) loginJic({ clearLocal: false });
    return token;
  };
  const onAvatarFile = async (file: File | undefined) => {
    if (!file) return;
    setAvatarError(null);
    const token = withToken();
    if (!token) return;
    setAvatarBusy(true);
    try {
      const blob = await prepareAvatar(file);
      const result = await uploadAvatar(blob, token);
      if (!result.ok || !result.avatar) throw new Error(result.error || "上传失败");
      setAvatar(result.avatar);
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : "上传失败");
    } finally {
      setAvatarBusy(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };
  const onAvatarRemove = async () => {
    setAvatarError(null);
    const token = withToken();
    if (!token) return;
    setAvatarBusy(true);
    try {
      const result = await deleteAvatar(token);
      if (!result.ok) throw new Error(result.error || "移除失败");
      setAvatar(null);
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : "移除失败");
    } finally {
      setAvatarBusy(false);
    }
  };

  const statusCopy = useMemo(() => {
    if (bound) return "已写入 FACE Registry · 全网可扫";
    if (livenessPassed) return "活体已通过 · 等待绑定身份";
    if (ownerChecked && !ownerAddress) return "名字尚未认领";
    return `等待 @${handle} 活体认领`;
  }, [bound, livenessPassed, ownerChecked, ownerAddress, handle]);

  const boundAddress = serverRecord?.address || null;

  return (
    <main className="face3-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />
      <header className="face3-header">
        <a className="face3-brand" href="/" aria-label="FACE3 home">
          <span className="face3-mark">F³</span>
          <span>FACE3</span>
        </a>
        <span className="face3-mvp-pill">LIVE ID</span>
      </header>

      <section className="face3-profile-grid">
        <article className="face3-profile-card">
          <div className="face3-sigil-wrap">
            {avatar ? <img className="fw-profile-avatar" src={avatar} alt={`@${handle}`} /> : <FaceSigil live={facePresent || livenessPassed || bound} />}
          </div>
          <div className="face3-profile-copy">
            <p className="face3-eyebrow">FACE3 HUMAN ADDRESS</p>
            <h1>@{handle}</h1>
            <p className="face3-handle">face.jic.io/<strong>{handle}</strong></p>
            <div className={`face3-status ${bound ? "is-verified" : ""}`}>
              <span className="face3-status-dot" />{statusCopy}
            </div>
            <p className="face3-thesis">脸负责找到你，活体证明你在场，0x 地址负责控制资产。</p>
            <div className="face3-id-row">
              <span>名字持有人</span><strong>{ownerAddress ? shortAddress(ownerAddress) : ownerChecked ? "未认领" : "查询中…"}</strong>
            </div>
            <div className="face3-id-row">
              <span>收款账户</span><strong>{boundAddress ? shortAddress(boundAddress) : "活体绑定后生效"}</strong>
            </div>
            {serverRecord?.identities && serverRecord.identities.length > 0 && (
              <div className="face3-identity-chips">
                {serverRecord.identities.map((identity) => (
                  <span key={`${identity.type}:${identity.value}`} className="face3-identity-chip">
                    <b>{identity.type === "jic" ? "JIC" : identity.type === "address" ? "0x" : identity.type.toUpperCase()}</b>
                    {identity.type === "address" ? shortAddress(identity.value) : identity.type === "jic" ? `@${identity.value}` : identity.value}
                    <i>✓</i>
                  </span>
                ))}
              </div>
            )}
          </div>
        </article>

        <article className="face3-claim-card">
          <div className="face3-section-label">CLAIM THIS FACE</div>
          <h2>用活体认领 @{handle}</h2>
          <p className="face3-muted">随机动作挑战在浏览器内完成，视频不上传。通过后生成 128 维人脸模板，写入 u.jic.io FACE Registry，任何人扫到这张脸就能给这个名字转 LOVE。脸不会生成钱包私钥。</p>

          {ownerChecked && !ownerAddress && (
            <div className="face3-instruction">
              <strong>@{handle} 还没有主人</strong>
              <small>先到 <a href={`https://u.jic.io/u`} style={{ color: "#58e8ff" }}>u.jic.io</a> 买下这个短名字，再回来绑定人脸</small>
            </div>
          )}

          <div className={`face3-camera ${checkState === "active" || checkState === "starting" ? "is-open" : ""}`}>
            <video ref={videoRef} playsInline muted />
            <div className="face3-camera-mask" />
            {checkState !== "active" && checkState !== "starting" && (
              <div className="face3-camera-placeholder"><FaceSigil live={livenessPassed} /></div>
            )}
            {checkState === "active" && <div className="face3-live-chip"><i />LIVE</div>}
          </div>

          <div className="face3-challenge-progress" aria-label="活体检测进度">
            {[0, 1, 2].map((index) => (
              <span key={index} className={index < step || livenessPassed ? "done" : index === step && checkState === "active" ? "current" : ""}>
                {index < step || livenessPassed ? "✓" : index + 1}
              </span>
            ))}
          </div>
          <div className="face3-instruction">
            <strong>{capturingTemplate ? "生成 Face ID" : checkState === "active" && currentChallenge ? CHALLENGE_COPY[currentChallenge] : livenessPassed ? "活体挑战完成" : "准备好后开启摄像头"}</strong>
            <small>{cameraMessage}</small>
          </div>

          {!livenessPassed && !bound && (
            <button className="face3-primary-button" type="button" onClick={startLiveness} disabled={checkState === "starting" || checkState === "active" || (ownerChecked && !ownerAddress)}>
              {checkState === "starting" ? "连接中…" : checkState === "active" ? "检测进行中" : checkState === "error" ? "重新开始活体检测" : "开始活体检测"}
            </button>
          )}
          {livenessPassed && !bound && (
            <button className="face3-primary-button" type="button" onClick={bindIdentity} disabled={binding}>
              {binding ? "正在写入 Registry…" : jicUser?.sub && getJicToken() ? `绑定 ${shortAddress(jicUser.sub)}` : "登录 JIC 并绑定"}
            </button>
          )}
          {bindError && <div className="face3-instruction"><strong style={{ color: "#ff8ba7" }}>绑定失败</strong><small>{bindError}</small></div>}
          {bound && (
            <div className="face3-success-panel"><span>✓</span><div><strong>@{handle} 已写入 FACE Registry</strong><small>任何人在 face.jic.io 扫这张脸，都会解析到 {boundAddress ? shortAddress(boundAddress) : "绑定地址"} 收 LOVE。</small></div></div>
          )}
          {bound && isOwner && (
            <label className="fw-toggle">
              <input type="checkbox" checked={wallPublic} disabled={wallBusy} onChange={(event) => toggleWall(event.target.checked)} />
              <span>
                <strong>{wallPublic ? `@${handle} 已在 face.jic.io 锚点墙公开` : `在 face.jic.io 锚点墙公开 @${handle}`}</strong>
                <small>墙上只显示名字，以及你上传的头像（没有就用名字生成的图案），不显示地址或脸纹模板。随时可以取消。</small>
                {wallError && <small style={{ color: "#ff8ba7" }}>{wallError}</small>}
              </span>
            </label>
          )}
          {bound && isOwner && (
            <div className="fw-avatar-row">
              <div className="fw-avatar-preview">{avatar ? <img src={avatar} alt="" /> : <NameSigil name={handle} />}</div>
              <div className="fw-avatar-copy">
                <strong>公开头像</strong>
                <small>展示在锚点墙和你的页面上。与脸纹分开存放，不用于识别；上传前会在手机上裁剪压缩，并去掉照片里的位置信息。</small>
                <div className="fw-avatar-actions">
                  <button type="button" onClick={() => avatarInputRef.current?.click()} disabled={avatarBusy}>
                    {avatarBusy ? "处理中…" : avatar ? "更换头像" : "上传头像"}
                  </button>
                  {avatar && <button type="button" className="is-quiet" onClick={onAvatarRemove} disabled={avatarBusy}>移除</button>}
                </div>
                {avatarError && <small style={{ color: "#ff8ba7" }}>{avatarError}</small>}
              </div>
              <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={(event) => onAvatarFile(event.target.files?.[0])} />
            </div>
          )}
          {bound && isOwner && !livenessPassed && (
            <button className="face3-primary-button" type="button" onClick={startLiveness} disabled={checkState === "starting" || checkState === "active"}>
              重新采集人脸模板
            </button>
          )}
        </article>
      </section>

      <section className="face3-flow">
        <div><span>01</span><strong>面纹地址</strong><small>face.jic.io/{handle}</small></div>
        <i>→</i>
        <div><span>02</span><strong>活体在场</strong><small>眨眼 + 随机转头</small></div>
        <i>→</i>
        <div><span>03</span><strong>0x 控制</strong><small>JIC 智能账户签名</small></div>
        <i>→</i>
        <div><span>♡</span><strong>LOVE 锚点</strong><small>礼物、记忆、正和消费</small></div>
      </section>

      <footer className="face3-footer">
        <span>FACE3 · Your face is the address, never the key.</span>
        <span>隐私优先 · 模板只存 Registry，服务端不外泄描述子</span>
      </footer>
      <JicFloatingWidget />
    </main>
  );
}
